import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import * as ArrayUtils from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { RootState } from 'af-reducers';

import Dropdown from 'af-components/Controls/Dropdown';
import Tooltip from 'af-components/Tooltip';

import { NUMBER_OF_FUTURE_YEAR_OPTIONS, YEAR_OPTION_START } from './constants';
import styles from './styles.module.scss';

type MomentType = ReturnType<typeof TimeUtils.parseMoment>;

interface DropdownItem {
	id: number;
	label: string;
}

const YEAR_OPTIONS: DropdownItem[] = ArrayUtils
	.range(YEAR_OPTION_START, new Date().getFullYear() + 1 + NUMBER_OF_FUTURE_YEAR_OPTIONS)
	.map((_year: number, _index: number) => ({ id: _index, label: _year.toString() }));

interface OwnProps {
	selected: Date;
	onChange: (selected: Date, startDate: Date, endDate: Date) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const FiscalPicker: React.FC<Props> = ({ selected: selectedDate, onChange, companyData }) => {
	const [selected, setSelected] = React.useState<MomentType>(TimeUtils.parseMoment(selectedDate));

	const { computedFiscalStartDate, computedFiscalEndDate } = React.useMemo(() => {
		const _computedFiscalStartDate = selected!.clone().subtract(1, 'year').month((companyData.fiscalYearStartMonth ?? 0) - 1).date(1).startOf('day');
		const _computedFiscalEndDate = _computedFiscalStartDate.clone().add(1, 'year').subtract(1, 'day').endOf('day');
		return {
			computedFiscalStartDate: _computedFiscalStartDate,
			computedFiscalEndDate: _computedFiscalEndDate,
		};
	}, [selected, companyData.fiscalYearStartMonth]);

	React.useEffect(() => {
		const _computedFiscalStartDate = TimeUtils.parseMoment(selectedDate)!.clone();
		const _computedFiscalEndDate = _computedFiscalStartDate.clone().add(1, 'year').subtract(1, 'day').endOf('day');
		setSelected(TimeUtils.parseMoment(_computedFiscalEndDate));
	}, [companyData.fiscalYearStartMonth, selectedDate]);

	const tooltipMessage = React.useMemo(() => `Fiscal Period: ${computedFiscalStartDate.format('LL')} - ${computedFiscalEndDate.format('LL')}`
		, [computedFiscalStartDate, computedFiscalEndDate]);

	const onYearChange = React.useCallback((item: DropdownItem) => {
		if (!selected) {
			throw new Error('No date used as reference point');
		}
		const fiscalYearStartMonth = companyData.fiscalYearStartMonth;
		const newSelected = selected.clone().year(+item.label);
		const fiscalStartDate = newSelected.clone().subtract(1, 'year').month(fiscalYearStartMonth ? fiscalYearStartMonth - 1 : 0).date(1).startOf('day');
		const fiscalEndDate = fiscalStartDate.clone().add(1, 'year').subtract(1, 'day').endOf('day');

		onChange(newSelected.toDate(), fiscalStartDate.toDate(), fiscalEndDate.toDate());
		setSelected(newSelected);
	}, [selected, companyData.fiscalYearStartMonth, onChange]);

	const renderDropdownElement = React.useCallback((item: DropdownItem) => {
		return item ? <div className={styles['dropdown-item']}>{item.label}</div> : <div />;
	}, []);

	const renderSelectedElement = React.useCallback((item: DropdownItem) => {
		return (
			<div className={styles['selected-fiscal-year']}>
				{item.label}
				<span className="icon-calendar" />
			</div>
		);
	}, []);

	return (
		<Tooltip message={tooltipMessage} >
			<Dropdown<DropdownItem>
				containerClassName={styles['date-picker-wrapper-fiscal']}
				defaultValue={{ id: 0, label: selected!.year().toString() }}
				id="year"
				labelKey="label"
				onValueChange={onYearChange}
				options={YEAR_OPTIONS}
				renderMenuItem={renderDropdownElement}
				renderSelected={renderSelectedElement}
				valueKey="id"
				withBorder={false}
			/>
		</Tooltip>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
}

const connector = connect(mapStateToProps, null);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(FiscalPicker);
